import React, { useCallback, useEffect, useState } from 'react';
import { money } from '../../lib/money';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  DescriptionList,
  EmptyState,
  KpiTile,
  PageContainer,
  PageHeader,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { requisitionPath } from '../../lib/slug';
import { marketlistApi, mlStatusMeta, marketListPath } from './marketlistApi';
import './marketlist.css';

export default function MarketListDetailPage() {
  const { id: routeRef } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission, properties } = useProperty();
  const canEdit = hasPermission('marketlist.create');
  const canFinalize = hasPermission('marketlist.finalize');

  const [ml, setMl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [submitOnFinalize, setSubmitOnFinalize] = useState(true);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setMl(await marketlistApi.get(routeRef));
    } catch (err) {
      setError(err?.message || t('Market list not found.'));
    } finally {
      setLoading(false);
    }
  }, [routeRef, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    catalogApi.listLocations().then((l) => { if (active) setLocations(l || []); }).catch(() => {});
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!ml?.id) return;
    const canonical = marketListPath(ml);
    if (`/market-list/${routeRef}` !== canonical) navigate(canonical, { replace: true });
  }, [ml, routeRef, navigate]);

  const locationName = (lid) => locations.find((l) => l.id === lid)?.name;

  const doFinalize = async () => {
    setBusy(true);
    try {
      const done = await marketlistApi.finalize(ml.id, submitOnFinalize);
      setFinalizeOpen(false);
      setMl(done);
      const count = done?.generatedRequisitions?.length || 0;
      toast.success(t('Finalized into {count} requisition(s)', { count }));
    } catch (err) {
      toast.error(t('Could not finalize'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doStartPurchasing = async () => {
    setBusy(true);
    try {
      const done = await marketlistApi.startPurchasing(ml.id);
      setMl(done);
      toast.success(t('Purchasing started'));
    } catch (err) {
      toast.error(t('Could not start purchasing'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doReopen = async () => {
    setBusy(true);
    try {
      const done = await marketlistApi.reopen(ml.id);
      setMl(done);
      toast.success(t('Market list reopened to draft'));
    } catch (err) {
      toast.error(t('Could not reopen'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doCancel = async () => {
    setBusy(true);
    try {
      const cancelled = await marketlistApi.cancel(ml.id, cancelReason.trim() || null);
      setCancelOpen(false);
      setCancelReason('');
      setMl(cancelled);
      toast.success(t('Market list cancelled'));
    } catch (err) {
      toast.error(t('Could not cancel'), { description: err?.message });
    } finally {
      setBusy(false);
    }
  };

  const doDiscard = async () => {
    setBusy(true);
    try {
      await marketlistApi.remove(ml.id);
      toast.success(t('Market list deleted'));
      navigate('/market-list');
    } catch (err) {
      toast.error(t('Could not delete'), { description: err?.message });
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !ml) {
    return (
      <PageContainer size="full">
        <EmptyState icon={<Icon.CircleAlert size={28} />} title={t("Couldn't load market list")} description={error || t('Not found.')}
          action={<Button variant="secondary" onClick={() => navigate('/market-list')}>{t('Back to market list')}</Button>} />
      </PageContainer>
    );
  }

  const statusMeta = mlStatusMeta(ml.status);
  const isDraft = ml.status === 'Draft';
  const isInProgress = ml.status === 'InProgress';
  const isFinalized = ml.status === 'Finalized';
  const isCancelled = ml.status === 'Cancelled';
  const isEditable = isDraft || isInProgress;
  const orderedLines = (ml.lines || []).filter((l) => Number(l.orderQuantity) > 0);

  const headerActions = isEditable ? (
    <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
      {canEdit && isDraft && <Button variant="ghost" size="sm" leadingIcon={<Icon.Trash size={14} />} onClick={() => setDiscardOpen(true)}>{t('Delete')}</Button>}
      {canEdit && <Button variant="ghost" size="sm" leadingIcon={<Icon.Ban size={14} />} onClick={() => setCancelOpen(true)}>{t('Cancel')}</Button>}
      {canEdit && isInProgress && <Button variant="ghost" size="sm" leadingIcon={<Icon.Refresh size={14} />} onClick={doReopen} loading={busy}>{t('Reopen')}</Button>}
      {canEdit && <Button variant="secondary" size="sm" leadingIcon={<Icon.Pencil size={14} />} onClick={() => navigate(marketListPath(ml, '/edit'))}>{t('Edit')}</Button>}
      {canEdit && isDraft && <Button variant="secondary" size="sm" leadingIcon={<Icon.ShoppingCart size={14} />} onClick={doStartPurchasing} loading={busy}>{t('Start purchasing')}</Button>}
      {canFinalize && <Button variant="primary" size="sm" leadingIcon={<Icon.CircleCheck size={14} />} onClick={() => setFinalizeOpen(true)}>{t('Finalize')}</Button>}
    </div>
  ) : null;

  const propertyName = ml.propertyId
    ? (properties.find((p) => p.id === ml.propertyId)?.name || null)
    : null;

  const overviewItems = [
    { term: t('Property'), description: propertyName || <span className="pp-cell-muted">{t('All properties')}</span> },
    { term: t('Status'), description: <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge> },
    { term: t('Outlet'), description: locationName(ml.locationId) || '—' },
    { term: t('Market date'), description: ml.marketDate ? new Date(ml.marketDate).toLocaleDateString() : '—' },
    { term: t('Created by'), description: ml.createdByName || '—' },
    { term: t('Created'), description: new Date(ml.createdAt).toLocaleString() },
    ...((isInProgress || isFinalized) && ml.purchasingStartedByName ? [{ term: t('Purchasing started by'), description: ml.purchasingStartedByName }] : []),
    ...(isFinalized ? [{ term: t('Finalized by'), description: ml.finalizedByName || '—' }] : []),
  ];

  const renderLine = (l) => {
    const assignedQuote = (l.quotes || []).find((q) => q.supplierId === l.assignedSupplierId);
    const cheapest = (l.quotes || [])
      .filter((q) => q.unitPrice)
      .reduce((a, b) => (a == null || Number(b.unitPrice.amount) < Number(a.unitPrice.amount) ? b : a), null);
    return (
      <div className="ml-line" key={l.lineId}>
        <div className="ml-line__top">
          <div className="ml-line__item">
            <div>
              <div className="ml-line__name">{l.description}</div>
              {l.sku && <div className="ml-line__sub">{l.sku}</div>}
            </div>
          </div>
          <div className="ml-line__stats">
            {l.parLevel != null && <div className="ml-stat"><span className="ml-stat__label">{t('Par')}</span><span className="ml-stat__value">{l.parLevel}</span></div>}
            {l.onHand != null && <div className="ml-stat"><span className="ml-stat__label">{t('On hand')}</span><span className="ml-stat__value">{l.onHand}</span></div>}
            <div className="ml-stat"><span className="ml-stat__label">{t('Ordered')}</span><span className="ml-stat__value">{l.orderQuantity}</span></div>
          </div>
        </div>
        {(l.quotes || []).length > 0 && (
          <div className="ml-quotes">
            <div className="ml-quotes__head"><span>{t('Vendor quotes')}</span></div>
            {l.quotes.map((q) => {
              const assigned = q.supplierId === l.assignedSupplierId;
              const isCheapest = cheapest && q.supplierId === cheapest.supplierId;
              return (
                <div className={`ml-quote ml-quote--readonly ${assigned ? 'is-assigned' : ''}`} key={q.supplierId}>
                  <span className="ml-quote__supplier-name">{q.supplierName || q.supplierId}</span>
                  <span className="ml-quote__price-value">{money(q.unitPrice)}</span>
                  <span className="ml-quote__badges">
                    {isCheapest && !assigned && <Badge tone="success">{t('Cheapest')}</Badge>}
                    {assigned && <Badge tone="brand" dot>{t('Assigned')}</Badge>}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        {l.assignedSupplierId && (
          <div className="ml-line__assigned">
            {t('Assigned to')} <strong>{l.assignedSupplierName || l.assignedSupplierId}</strong>
            {assignedQuote?.unitPrice && <> {t('at {price}/unit', { price: money(assignedQuote.unitPrice) })}</>}
          </div>
        )}
      </div>
    );
  };

  return (
    <PageContainer size="full">
      <Button variant="ghost" size="sm" leadingIcon={<Icon.ArrowLeft size={15} />} onClick={() => navigate('/market-list')} style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}>
        {t('Market list')}
      </Button>

      <PageHeader
        title={ml.title}
        description={
          <span>
            <span className="req-detail__number">{ml.number}</span>
            {propertyName && <> · {propertyName}</>}
          </span>
        }
        icon={<Icon.ClipboardList size={22} />}
        actions={headerActions}
      />

      {isDraft && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="info">
            {t('This market list is a draft. Add the items to order, then start purchasing to capture vendor prices and assign a vendor per line.')}
          </Banner>
        </div>
      )}

      {isInProgress && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">
            {t('Purchasing is in progress. Edit the list to capture prices, assign vendors, or substitute items. When buying is complete, a purchasing officer finalizes it into one requisition per assigned vendor.')}
          </Banner>
        </div>
      )}

      {isCancelled && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="danger">
            {t('This market list was cancelled')}{ml.cancelledByName ? ` ${t('by {name}', { name: ml.cancelledByName })}` : ''}{ml.cancelledAt ? ` ${t('on {date}', { date: new Date(ml.cancelledAt).toLocaleString() })}` : ''}.{ml.cancelledReason ? ` ${t('Reason: {reason}', { reason: ml.cancelledReason })}` : ''}
          </Banner>
        </div>
      )}

      {isFinalized && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="success">
            {t('Finalized into {count} requisition(s)', { count: ml.generatedRequisitions?.length || 0 })}{ml.finalizedAt ? ` ${t('on {date}', { date: new Date(ml.finalizedAt).toLocaleString() })}` : ''}. {t('They are routed through the normal approval pipeline.')}
          </Banner>
        </div>
      )}

      <div className="req-kpis">
        <KpiTile label={t('Outlet')} value={locationName(ml.locationId) || '—'} />
        <KpiTile label={t('Lines to order')} value={orderedLines.length} />
        <KpiTile label={t('Vendors assigned')} value={new Set(orderedLines.map((l) => l.assignedSupplierId).filter(Boolean)).size} />
        <KpiTile label={t('Status')} value={t(statusMeta.label)} />
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <DescriptionList items={overviewItems} layout="stacked" columns={2} />
          </Card.Body>
        </Card>

        {ml.notes && (
          <Card>
            <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
            <Card.Body>{ml.notes}</Card.Body>
          </Card>
        )}

        {isFinalized && (ml.generatedRequisitions || []).length > 0 && (
          <Card>
            <Card.Header>
              <Card.Title>{t('Generated requisitions')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>{t('One requisition per assigned vendor, routed for approval.')}</div>
            </Card.Header>
            <Card.Body>
              <div className="req-po-links">
                {ml.generatedRequisitions.map((r) => (
                  <button key={r.requisitionId} type="button" className="req-po-link" onClick={() => navigate(requisitionPath({ number: r.number, id: r.requisitionId }))}>
                    <span className="req-po-link__number">{r.number}</span>
                    {r.supplierName && <span className="req-po-link__supplier">{r.supplierName}</span>}
                    <Icon.ChevronRight size={16} />
                  </button>
                ))}
              </div>
            </Card.Body>
          </Card>
        )}

        <Card>
          <Card.Header>
            <Card.Title>{t('Items & quotes')}</Card.Title>
            <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
              {isEditable ? t('Vendor prices captured for the day; the assigned vendor fulfils each line.') : t('The vendor quotes and assignments captured for this market list.')}
            </div>
          </Card.Header>
          <Card.Body>
            <div className="ml-lines">
              {(ml.lines || []).map(renderLine)}
            </div>
          </Card.Body>
        </Card>
      </div>

      <ConfirmDialog
        open={finalizeOpen}
        title={t('Finalize market list')}
        description={t('This creates one requisition per assigned vendor for the ordered lines. The market list becomes read-only.')}
        confirmLabel={t('Finalize')}
        tone="primary"
        loading={busy}
        onConfirm={doFinalize}
        onCancel={() => (busy ? null : setFinalizeOpen(false))}
      >
        <Checkbox
          checked={submitOnFinalize}
          onChange={(e) => setSubmitOnFinalize(e.target.checked)}
          label={t('Submit the requisitions for approval')}
          hint={t('Leave unchecked to create them as drafts so prices can still be refined before submitting.')}
        />
      </ConfirmDialog>

      <ConfirmDialog
        open={cancelOpen}
        title={t('Cancel market list')}
        description={t('Mark this market list as cancelled. You can still delete a draft afterwards.')}
        confirmLabel={t('Cancel market list')}
        tone="danger"
        loading={busy}
        onConfirm={doCancel}
        onCancel={() => (busy ? null : setCancelOpen(false))}
      >
        <Textarea rows={2} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder={t('Reason (optional)')} />
      </ConfirmDialog>

      <ConfirmDialog
        open={discardOpen}
        title={t('Delete market list')}
        description={t("Permanently delete this market list. This can't be undone.")}
        confirmLabel={t('Delete')}
        tone="danger"
        loading={busy}
        onConfirm={doDiscard}
        onCancel={() => (busy ? null : setDiscardOpen(false))}
      />
    </PageContainer>
  );
}
